import type { InvitationPreview } from "@planerius/contracts/cabinet-auth"
import { systemRole } from "@planerius/contracts/cabinet-roles"

import type { ApiProblem } from "../../api/problem"

/**
 * Экран принятия приглашения (1-Э1.5) как данные, отдельно от разметки.
 *
 * ГЛАВНАЯ РАЗВИЛКА — НЕ «НОВЫЙ ИЛИ СТАРЫЙ», А «ЧЕМ ДОКАЗЫВАЕТСЯ, ЧТО ЭТО ВЫ». Новый человек
 * доказывает это самой ссылкой: она пришла ему. Человеку, у которого учётка уже есть,
 * ссылки мало — её получает и тот, кто приглашает, прямо в ответе на выпуск. Поэтому
 * макетное «Принять и добавить салон» живёт только ПОСЛЕ входа, а до входа кнопка честно
 * говорит «Войти и принять» (сервер отказал бы 409 invitation_sign_in_required).
 *
 * Тексты — из макета «Мастер, доступ и заглушка — П1», фрейм 1-Э1.5. Чего в макете нет
 * (отозвано, ссылка не работает, приглашение только на телефон, итог после принятия),
 * написано тем же голосом и вынесено вопросом дизайнеру.
 */

/** Кто сейчас вошёл в этом браузере — если вошёл. */
export interface InviteSession {
  /** null — вошёл, но салон не выбран: профиля с именем в этом состоянии нет. */
  readonly displayName: string | null
}

function signedInAs(session: InviteSession): string {
  return session.displayName === null ? "Вы уже вошли в кабинет" : `Вы вошли как ${session.displayName}`
}

export interface RoleCard {
  readonly name: string
  /** Описание одной фразой — только у системной роли; у своей роли салона его нет. */
  readonly summary: string | null
  readonly branches: string
  /** Роль требует второго фактора, а завести его в кабинете пока нельзя. */
  readonly mfaWarning: string | null
}

export type InviteView =
  | { readonly kind: "invalid"; readonly heading: string; readonly text: string }
  | { readonly kind: "revoked"; readonly heading: string; readonly text: string }
  | { readonly kind: "expired"; readonly heading: string; readonly text: string }
  | { readonly kind: "accepted"; readonly heading: string; readonly text: string; readonly button: string }
  | {
      readonly kind: "form"
      /**
       * new — заводит вход здесь же; sign_in — учётка есть, нужно войти; signed_in — уже
       * вошёл, принимает из-под своей учётки.
       */
      readonly mode: "new" | "sign_in" | "signed_in"
      readonly heading: string
      readonly role: RoleCard
      readonly needsEmail: boolean
      /** Плашка под карточкой роли: кто вошёл и что сделает кнопка. */
      readonly notice: string | null
      /** Вошёл кто-то другой, а приглашение — для нового человека: второй путь ссылкой. */
      readonly alsoAsSignedIn: string | null
      /** Вошли, но, может быть, не под той учётной записью: выйти и войти под нужной. */
      readonly switchAccount: string | null
      readonly button: string
    }

export const TEXT_PASSWORD_HINT = "Не короче 10 знаков"
export const TEXT_TWO_CONSENTS = "Это два отдельных документа — отметьте оба."
export const MIN_PASSWORD_LENGTH = 10

const INVALID: InviteView = {
  kind: "invalid",
  heading: "Ссылка не работает",
  text: "Ссылка на приглашение недействительна или устарела. Скопируйте её из сообщения целиком или попросите прислать заново.",
}

/** «2 сентября» — дата без дня недели, как в макете. Неразобранная дата — «—», а не падение. */
export function formatInviteDate(instant: string | null, timeZone?: string): string {
  if (instant === null) return "—"
  const date = new Date(instant)
  if (Number.isNaN(date.getTime())) return "—"
  return new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long", timeZone }).format(date)
}

export function roleCard(preview: InvitationPreview): RoleCard {
  const summary = preview.roleCode === null ? null : (systemRole(preview.roleCode)?.summary ?? null)
  const where =
    preview.branchNames.length === 0
      ? "Все филиалы салона, включая будущие."
      : preview.branchNames.length === 1
        ? `Филиал: ${preview.branchNames[0] ?? ""}.`
        : `Филиалы: ${preview.branchNames.join(", ")}.`
  return {
    name: preview.roleName,
    summary,
    branches: `${where} Роль и филиалы меняет салон — не вы.`,
    mfaWarning: preview.roleRequiresMfa
      ? "Для этой роли салон требует второй фактор входа. Настроить его в кабинете пока нельзя, поэтому войти не получится — предупредите того, кто вас приглашает."
      : null,
  }
}

function inviter(preview: InvitationPreview): string {
  return preview.invitedByName ?? "Салон"
}

export function inviteView(input: {
  readonly preview: InvitationPreview | null
  /** Ссылка не найдена (404) или в адресе нет токена. */
  readonly invalid: boolean
  readonly session: InviteSession | null
  readonly timeZone?: string
}): InviteView | null {
  if (input.invalid) return INVALID
  const preview = input.preview
  if (preview === null) return null

  const salon = `«${preview.tenantName}»`

  if (preview.status === "accepted") {
    return {
      kind: "accepted",
      heading: "Приглашение уже принято",
      text: `Вы приняли его ${formatInviteDate(preview.acceptedAt, input.timeZone)}. Просто войдите — это не ошибка.`,
      button: "Войти",
    }
  }
  if (preview.status === "expired") {
    const who = preview.invitedByName === null ? `Приглашение в ${salon}` : `Приглашал ${preview.invitedByName}, ${salon}`
    return {
      kind: "expired",
      heading: "Срок приглашения истёк",
      text: `${who}, ${formatInviteDate(preview.invitedAt, input.timeZone)}. Попросите отправить приглашение ещё раз — это одна кнопка в разделе «Сотрудники».`,
    }
  }
  if (preview.status === "revoked") {
    return {
      kind: "revoked",
      heading: "Приглашение отозвано",
      text: `${salon} отозвал это приглашение или выпустил новое — работает только последняя ссылка. Попросите прислать её ещё раз.`,
    }
  }

  const heading =
    preview.invitedByName === null
      ? `Вас приглашают в ${salon}`
      : `${inviter(preview)} приглашает вас в ${salon}`
  const role = roleCard(preview)

  if (input.session !== null && preview.signInRequired) {
    return {
      kind: "form",
      mode: "signed_in",
      heading,
      role,
      needsEmail: false,
      notice: `${signedInAs(input.session)}. Если приглашение адресовано этой учётной записи — примите его, и ${salon} добавится к вашим салонам; пароль задавать не нужно. Если нет — войдите под своей.`,
      alsoAsSignedIn: null,
      switchAccount: "Войти под другой учётной записью",
      button: "Принять и добавить салон",
    }
  }

  if (preview.signInRequired) {
    return {
      kind: "form",
      mode: "sign_in",
      heading,
      role,
      needsEmail: false,
      notice: "У вас уже есть учётная запись. Войдите — и приглашение добавит этот салон к вашим. Пароль задавать не нужно: один человек — одна учётная запись.",
      alsoAsSignedIn: null,
      switchAccount: null,
      button: "Войти и принять",
    }
  }

  return {
    kind: "form",
    mode: "new",
    heading,
    role,
    needsEmail: preview.needsEmail,
    notice:
      input.session === null
        ? null
        : `${signedInAs(input.session)} в этом браузере. Приглашение — для новой учётной записи: заполните форму, и ваш вход не изменится.`,
    // Принять из-под своего входа сервер позволит только приглашение на телефон, у которого
    // ещё нет учётки (needsEmail); на почту — только учётке с этой почтой, а раз форма
    // «новый человек», такой учётки нет. Предлагать путь, который всегда отказывает, нельзя.
    alsoAsSignedIn:
      input.session === null || !preview.needsEmail
        ? null
        : input.session.displayName === null
          ? "Это приглашение для меня — принять под своей учётной записью"
          : `Это приглашение для меня — принять как ${input.session.displayName}`,
    switchAccount: null,
    button: "Принять приглашение",
  }
}

export interface InviteForm {
  readonly displayName: string
  readonly email: string
  readonly password: string
  readonly terms: boolean
  readonly staffPersonalData: boolean
}

export interface InviteFormErrors {
  readonly displayName: string | null
  readonly email: string | null
  readonly password: string | null
  readonly consents: string | null
}

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Ошибки формы ДО отправки. Сервер проверит то же самое, но человек должен узнать
 * о пропущенной галочке сразу, а не после круга по сети с ответом 400 «validation».
 * Длина пароля считается после обрезки пробелов — так же, как на сервере
 * (password.service.ts), иначе «9 знаков и пробел» пройдут здесь и упадут там.
 */
export function inviteFormErrors(form: InviteForm, mode: "new" | "sign_in" | "signed_in", needsEmail: boolean): InviteFormErrors {
  const consents = form.terms && form.staffPersonalData ? null : TEXT_TWO_CONSENTS
  if (mode !== "new") {
    return { displayName: null, email: null, password: null, consents: mode === "signed_in" ? consents : null }
  }
  return {
    displayName: form.displayName.trim().length === 0 ? "Напишите, как к вам обращаться" : null,
    email:
      !needsEmail ? null
      : form.email.trim().length === 0 ? "Укажите почту: по ней вы будете входить в кабинет"
      : EMAIL_SHAPE.test(form.email.trim()) ? null
      : "Похоже, в адресе опечатка",
    password:
      form.password.trim().length < MIN_PASSWORD_LENGTH ? `Пароль короче ${MIN_PASSWORD_LENGTH} знаков` : null,
    consents,
  }
}

export function hasErrors(errors: InviteFormErrors): boolean {
  return errors.displayName !== null || errors.email !== null || errors.password !== null || errors.consents !== null
}

/**
 * Что делать с отказом сервера при принятии.
 *
 * reload — состояние приглашения поменялось (приняли, истекло, отозвали, учётка уже есть):
 * перечитать превью, и экран сам покажет правильное состояние, а не текст ошибки поверх
 * формы, которой уже нечего делать. field — ошибка конкретного поля. message — объяснить.
 */
export type AcceptProblemAction =
  | { readonly kind: "reload" }
  | { readonly kind: "field"; readonly field: "password" | "email"; readonly text: string }
  | { readonly kind: "message"; readonly text: string }

export function acceptProblemAction(
  problem: ApiProblem,
  context?: { readonly mode: "new" | "sign_in" | "signed_in"; readonly needsEmail: boolean },
): AcceptProblemAction {
  // Приглашение на телефон, а введённая почта уже занята чужой (или своей) учёткой: превью
  // ищет по телефону и снова покажет ту же форму — перечитывать бессмысленно, это ошибка поля.
  if (problem.code === "invitation_sign_in_required" && context?.mode === "new" && context.needsEmail) {
    return {
      kind: "field",
      field: "email",
      text: "Эта почта уже занята учётной записью. Если она ваша — войдите под ней и откройте ссылку ещё раз; если нет — укажите другую.",
    }
  }
  switch (problem.code) {
    case "invitation_wrong_account":
      return { kind: "message", text: problem.detail ?? "Приглашение адресовано другой учётной записи. Войдите под ней." }
    case "invitation_already_accepted":
    case "invitation_expired":
    case "invitation_invalid":
    case "invitation_sign_in_required":
      return { kind: "reload" }
    case "password_weak":
      return { kind: "field", field: "password", text: problem.detail ?? "Пароль слишком простой" }
    case "invitation_email_required":
      return { kind: "field", field: "email", text: problem.detail ?? "Укажите почту для входа" }
    case "offline":
    case "timeout":
      return { kind: "message", text: "Нет связи с сервисом. Проверьте интернет и нажмите ещё раз — приглашение никуда не денется." }
    default:
      return { kind: "message", text: problem.detail ?? problem.title }
  }
}
